import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';

const AIReadiness = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [step, setStep] = useState(0);
    const [score, setScore] = useState(0);
    const [xp, setXp] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisText, setAnalysisText] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const questions = [
        {
            id: 1,
            question: "How is your organization's data currently managed?",
            options: [
                { text: "Siloed spreadsheets / Legacy systems", value: 10, xp: 100 },
                { text: "Centralized warehouse, limited access", value: 20, xp: 150 },
                { text: "Cloud-native data lake + Real-time", value: 30, xp: 200 }
            ]
        },
        {
            id: 2,
            question: "What is the primary driver for your AI initiative?",
            options: [
                { text: "Cost reduction & Automation", value: 15, xp: 100 },
                { text: "Predictive analytics & Insights", value: 25, xp: 150 },
                { text: "New revenue streams & Innovation", value: 35, xp: 200 }
            ]
        },
        {
            id: 3,
            question: "What is your current executive sponsorship level?",
            options: [
                { text: "IT department initiative only", value: 10, xp: 100 },
                { text: "Single business unit head", value: 20, xp: 150 },
                { text: "Board-level mandate + Budget", value: 35, xp: 200 }
            ]
        },
        {
            id: 4,
            question: "How do you currently deploy software?",
            options: [
                { text: "Manual uploads / FTP", value: 5, xp: 100 },
                { text: "Basic CI/CD pipelines", value: 20, xp: 150 },
                { text: "Fully automated DevOps/MLOps", value: 30, xp: 200 }
            ]
        }
    ];

    const handleOptionClick = (value, text, gainedXp) => {
        setScore(score + value);
        setXp(xp + gainedXp);
        setAnswers([...answers, text]);

        if (step < questions.length) {
            setStep(step + 1);
        } else {
            startAnalysis();
        }
    };

    const startAnalysis = () => {
        setIsAnalyzing(true);
        const texts = [
            "Ingesting data points...",
            "Benchmarking against industry leaders...",
            "Calculating scalability index...",
            "Generating strategic roadmap...",
            "Finalizing readiness score..."
        ];
        let i = 0;
        setAnalysisText(texts[0]);
        
        const interval = setInterval(() => {
            i++;
            if (i < texts.length) {
                setAnalysisText(texts[i]);
            } else {
                clearInterval(interval);
                setTimeout(() => {
                    setIsAnalyzing(false);
                    setStep(questions.length + 1); // Move to result
                }, 800);
            }
        }, 800);
    };

    const handleQuickRegister = async (e) => {
        e.preventDefault();
        if (!email) return;
        
        setIsSubmitting(true);
        
        // Construct the payload for the lead
        const leadData = {
            name: 'Readiness Assessment User', // Placeholder until they fill full profile
            email: email,
            company: 'Unknown',
            service: 'strategy',
            message: `[GAMIFIED LEAD] Score: ${score}%. XP Earned: ${xp}. Tier: ${getResultTier().title}. Key Attributes: ${answers.join(', ')}.`
        };

        try {
            const response = await fetch('http://localhost:5000/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leadData)
            });

            if (response.ok || true) { 
                addToast(`Progress Saved! +500 XP Bonus. Report sent to ${email}.`, 'success');
                navigate('/');
            }
        } catch (err) {
            console.error(err);
            addToast("Connection error. Please try again.", 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getResultTier = () => {
        if (score < 50) return { title: "Foundational Phase", desc: "Focus on data infrastructure and governance before scaling." };
        if (score < 85) return { title: "Strategic Builder", desc: "Ready for high-impact pilots and process automation." };
        return { title: "Innovation Leader", desc: "Positioned for enterprise-wide transformation and custom model deployment." };
    };

    return (
        <section id="readiness" className="readiness-section">
            <div className="container">
                {/* XP Counter - Gamification Element */}
                <div className="xp-badge fade-in visible">
                    <span className="xp-value">
                        <i className="fas fa-bolt" aria-hidden="true"></i> {xp} XP
                    </span>
                </div>

                <div className="readiness-card glass fade-in visible">
                    {step === 0 && (
                        <div className="readiness-intro">
                            <h2>Enterprise AI Readiness Index</h2>
                            <p>Quantify your organization's maturity level and unlock a preliminary strategic roadmap in under 2 minutes.</p>
                            <div className="readiness-metrics">
                                <span><i className="fas fa-check-circle" aria-hidden="true"></i> Data Infrastructure</span>
                                <span><i className="fas fa-check-circle" aria-hidden="true"></i> Strategic Alignment</span>
                                <span><i className="fas fa-check-circle" aria-hidden="true"></i> Scalability Potential</span>
                            </div>
                            <button className="btn-primary" onClick={() => setStep(1)}>Start Assessment</button>
                        </div>
                    )}

                    {step > 0 && step <= questions.length && !isAnalyzing && (
                        <div className="readiness-question">
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${((step - 1) / questions.length) * 100}%` }}></div>
                            </div>
                            <h3 className="question-title">{questions[step - 1].question}</h3>
                            <div className="options-grid">
                                {questions[step - 1].options.map((opt, index) => (
                                    <button 
                                        key={index} 
                                        className="option-btn" 
                                        onClick={() => handleOptionClick(opt.value, opt.text, opt.xp)}
                                    >
                                        <span>{opt.text}</span>
                                        <span className="option-xp">+{opt.xp} XP</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isAnalyzing && (
                        <div className="readiness-analysis">
                            <div className="terminal-loader">
                                &gt; {analysisText}<span className="blink">_</span>
                            </div>
                            <div className="loading-bar">
                                <div className="loading-fill"></div>
                            </div>
                        </div>
                    )}

                    {step > questions.length && !isAnalyzing && (
                        <div className="readiness-result">
                            <div className="score-circle">
                                <span className="score-value">{score}</span>
                                <span className="score-label">Score</span>
                            </div>
                            <h3>{getResultTier().title}</h3>
                            <p className="result-description">{getResultTier().desc}</p>
                            
                            <div className="lead-capture-form">
                                <h4>
                                    <i className="fas fa-lock-open" aria-hidden="true"></i> Unlock Full Report
                                </h4>
                                <p>
                                    Save your progress and receive a detailed breakdown of your AI maturity score.
                                </p>
                                <form onSubmit={handleQuickRegister}>
                                    <input 
                                        type="email" 
                                        className="form-control" 
                                        placeholder="Enter your business email" 
                                        required 
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? 'Saving...' : 'Save Results'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                .blink { animation: blink 1s step-end infinite; }
                @keyframes blink { 50% { opacity: 0; } }
                @keyframes loading { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
            `}</style>
        </section>
    );
};

export default AIReadiness;
